import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Can } from '../../contexts/PermissionsContext'
import { useAddContractAsset, useContract, useRemoveContractAsset } from '../../hooks/useQueries'
import { AssetPicker } from '../AssetPicker'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { apiErrorMessage, invalidateAssembly } from './assembly-api'
import { useQueryClient } from '@tanstack/react-query'

type LinkedAsset = { asset_uid: string; asset?: { name?: string } }

/**
 * The assets a resolution concerns. Every resolution inherits its minutes'
 * assets (the syndicate); the ones it names itself are the links that say what
 * it is about. The extraction only proposes them (a clause awaiting approval):
 * approving the resolution applies them. Spec 2026-10-10-08 §2.
 */
export function ResolutionAssets({
    org,
    minutesUid,
    resolutionUid,
    proposed,
    closed,
}: {
    org: string
    minutesUid: string
    resolutionUid: string
    /** The proposed asset uids (clause concerned_asset_uids), not yet linked. */
    proposed: string[]
    closed: boolean
}) {
    const { t } = useTranslation('assemblies')
    const queryClient = useQueryClient()
    const { data: resolution } = useContract(org, resolutionUid, ['assets'])
    const { data: minutes } = useContract(org, minutesUid, ['assets'])
    const add = useAddContractAsset(org, resolutionUid)
    const remove = useRemoveContractAsset(org, resolutionUid)
    const [picking, setPicking] = useState(false)

    const linked = ((resolution as { assets?: LinkedAsset[] } | undefined)?.assets ?? [])
    const inherited = new Set(((minutes as { assets?: LinkedAsset[] } | undefined)?.assets ?? []).map((a) => a.asset_uid))
    const own = linked.filter((a) => !inherited.has(a.asset_uid))
    const inheritedNames = linked.filter((a) => inherited.has(a.asset_uid))
    const pending = proposed.filter((uid) => !linked.some((a) => a.asset_uid === uid))
    const error = apiErrorMessage(add.error || remove.error)
    const done = () => invalidateAssembly(queryClient)

    return (
        <Card data-testid="resolution-concerns">
            <CardHeader>
                <CardTitle className="text-base">{t('panel.concerns')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                {own.length === 0 ? (
                    <p className="text-muted-foreground" data-testid="resolution-concerns-building">
                        {t('panel.concerns_building', { names: inheritedNames.map((a) => a.asset?.name ?? '').filter(Boolean).join(', ') })}
                    </p>
                ) : (
                    <ul className="flex flex-wrap gap-2" data-testid="resolution-concerns-list">
                        {own.map((a) => (
                            <li key={a.asset_uid}>
                                <Badge variant="outline" className="gap-1" data-testid={`resolution-concerns-${a.asset_uid}`}>
                                    {a.asset?.name ?? a.asset_uid}
                                    <Can perm="contracts.write">
                                        <button
                                            type="button"
                                            className="ml-1 text-muted-foreground hover:text-destructive disabled:opacity-40"
                                            aria-label={t('panel.concerns_remove')}
                                            title={t('panel.concerns_remove')}
                                            disabled={closed || remove.isPending}
                                            onClick={() => remove.mutate(a.asset_uid, { onSuccess: done })}
                                            data-testid={`resolution-concerns-remove-${a.asset_uid}`}
                                        >
                                            ×
                                        </button>
                                    </Can>
                                </Badge>
                            </li>
                        ))}
                    </ul>
                )}
                {pending.length > 0 && (
                    <p className="text-muted-foreground" data-testid="resolution-concerns-proposed">
                        {t('panel.concerns_proposed', { count: pending.length })}
                    </p>
                )}
                {error && <p className="text-destructive" data-testid="resolution-concerns-error">{error}</p>}
                <Can perm="contracts.write">
                    {picking ? (
                        <div className="max-w-md space-y-2" data-testid="resolution-concerns-picker">
                            <AssetPicker
                                org={org}
                                testId="resolution-concerns-asset"
                                onChange={(uid) => {
                                    add.mutate({ asset_uid: uid }, { onSuccess: () => { setPicking(false); done() } })
                                }}
                            />
                            <Button size="sm" variant="ghost" onClick={() => setPicking(false)} data-testid="resolution-concerns-cancel">
                                {t('cancel')}
                            </Button>
                        </div>
                    ) : (
                        <Button size="sm" variant="outline" disabled={closed} onClick={() => setPicking(true)} data-testid="resolution-concerns-add">
                            {t('panel.concerns_add')}
                        </Button>
                    )}
                </Can>
            </CardContent>
        </Card>
    )
}
